import React, { useRef, useState, useCallback, useEffect } from 'react'

import {
    Box,
} from '@mui/material';

import { useNavigate, Link } from 'react-router-dom';

import CommonBreadcumbs from '../../common/CommonBreadcumbs';
import DataGridToolbar from '../../common/DataGridToolbar';
import DataGridTable from '../../common/DataGridTable';
import CommonRestricted from '../../common/CommonRestricted';

import { icons } from '../../common/CommonIcons';
import { useItems, itemTypes, targetTypes } from '../../../hooks/api/useItems';
import { useSystemPrompt } from '../../../hooks/useSystemPrompt';
import { useDataGridParams } from '../../../hooks/useDataGridParams';
import { useSystemRoutes, routeTypes } from '../../../hooks/useSystemRoutes';
import { useSystemHead, headTitles } from '../../../hooks/useSystemHead';
import { usePermissions, scopeIds, scopeTypes, functionTypes } from '../../../hooks/usePermissions';
import { useTranslations } from '../../../hooks/useTranslations';

export default function ListBrands({
    routeType
}) {
    const { t } = useTranslations({ ns: ["common", "@product"] })

    const navigate = useNavigate();

    const { promptMessage } = useSystemPrompt();
    const { list, remove } = useItems({ itemType: itemTypes.brand, targetType: targetTypes.brand });

    const [ selectedIds, setSelectedIds ] = useState([])
    const [ refreshDataGrid, setRefreshDataGrid ] = useState(0)

    const { defineColumns, columns, filterValues, getRenderColumnLink, getRenderColumnBooleanDisplay, getRenderColumnTextDisplay } = useDataGridParams('id')
    const { isPermissionAllow } = usePermissions(routeType, scopeIds.product.id, scopeTypes.product.brand.id)

    const gridRef = React.createRef();
    const { getRoute } = useSystemRoutes(routeType);
    const { getHtmlHead } = useSystemHead(routeType)

    const resetDataGridTableSelection = () => {
        setSelectedIds([])
    }

    const fetchData = async (props) => {

        resetDataGridTableSelection();

        try{

            const result = await list(props)

            return {
                data: result?.data?.item_brands,
                count: result?.data?.total
            }

        }catch(error){

            promptMessage(false, `Error getting results (${error?.message})`)
            
            return {
                data: [],
                count: 0
            }
        }  
        
    }

    const handleDelete = async () => {

        try{
            let result = await remove({ ids: selectedIds })

            promptMessage(true, result?.message)
            setRefreshDataGrid(val => val+1)

        }catch(error){
            promptMessage(false, error?.message)
        }

    }

    useEffect (()=>{ 

        defineColumnsView()
       
    }, [])

    const defineColumnsView = () => {
        defineColumns([
            { 
                name: 'name', 
                header: t('brandName'), 
                renderValue: ({ value, cellProps }) => {

                    if (isPermissionAllow({ functionType: functionTypes.pageUpdate.id }) === false) {
                        return getRenderColumnTextDisplay(value)
                    }

                    let path = getRoute(`products/brand/update/${cellProps.data.id}`);

                    return getRenderColumnLink(path, value)

                }
            },    
            { 
                name: 'description', 
                header: t('description')
            },
            { 
                name: 'status', 
                header: t('status'),
                renderValue: ({ cellProps, value }) => getRenderColumnBooleanDisplay(value, [t('enabled'), t('disabled')], ["primary", "default"], "")
            }
        ])
    }

    const RenderToolbar = () => {

        const getToolbarMainMenus = () => {

            let props = [];

            if (isPermissionAllow({ functionType: functionTypes.pageNew.id })) {
                props.push({
                    displayIconName: icons.plus,
                    onClick: () => {
                        navigate(getRoute("products/brand/new"));
                    }
                })
            }

            return props
        }

        const getActionOnClick = () => {

            let handleFunction = null

            if (isPermissionAllow({ functionType: functionTypes.remove.id })) {
                handleFunction = handleDelete
            }

            return handleFunction
        }

        return (
            <DataGridToolbar
                selectedRowCount={
                    selectedIds.length
                }
                mainMenus={
                    getToolbarMainMenus()
                }
                actionOnClickRemove={
                    getActionOnClick()
                }
                actionMenus={[]}
            />
        )
    }

    const RenderDataGridTable = () => {

        return (
            <DataGridTable
                ref={gridRef}
                refresh={refreshDataGrid}
                fetchData={fetchData}
                dataGrid={{
                    idProperty: "id",
                    style: {
                        minHeight: 600
                    },
                    pageSizes: [20, 50, 100],
                    defaultLimit: 10,
                    defaultSkip: 0,
                    checkboxColumn: 
                        isPermissionAllow({ functionType: functionTypes.remove.id }),
                    columns,
                    defaultFilterValue: filterValues,
                    onSelectionChange: (items) => {
                        setSelectedIds(
                            items
                        )
                    }
                }}
                
            />
        )
    }

    return (
        <Box>
            {  
                getHtmlHead({ routeType, title: headTitles.brands })
            }

            <CommonBreadcumbs
                routeType={routeType}
                pageTitle={t('brands')}
                breadcrumbs={[
                    {
                        displayText: t('brands'),
                        props: {
                            // underline: "hover",
                            // href: getRoute("items")
                        }
                    },
                ]}
            />

            {
                isPermissionAllow({ functionType: functionTypes.pageList.id }) ?
                    <>
                        {
                            RenderToolbar()
                        }
                        {
                            RenderDataGridTable()
                        }
                    </>
                    :
                    (
                        <CommonRestricted />
                    )
            }

        </Box>
    )
}
